import { useState, useEffect, useRef } from "react";
import { cn } from "../../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";

export function DropdownWithTextField({
  item,
  value,
  error,
  helperText,
  disabled,
  onValueChange,
  className,
}) {
  const options = item?.options ?? item?.values ?? [];
  const placeholder = item?.placeholder || "Select an option";
  
  // Ref to track when "Other" is being selected (synchronous check)
  const selectingOtherRef = useRef(false);
  
  // Determine if current value is "Other" or a predefined option
  const isOtherValue = (val) => {
    if (!val) return false;
    const optionValues = options.map(opt => 
      typeof opt === "object" ? String(opt.value ?? "") : String(opt)
    );
    return !optionValues.includes(String(val));
  };

  // Initialize state based on current value
  const [selectedValue, setSelectedValue] = useState(() => {
    if (!value) return "";
    return isOtherValue(value) ? "Other" : String(value);
  });
  
  const [customText, setCustomText] = useState(() => {
    return isOtherValue(value) ? String(value) : "";
  });

  const [isOpen, setIsOpen] = useState(false);
  const [showInput, setShowInput] = useState(() => {
    return selectedValue === "Other";
  });

  // Sync with external value changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      if (isOtherValue(value)) {
        setSelectedValue("Other");
        setCustomText(String(value));
        setShowInput(true);
      } else {
        setSelectedValue(String(value));
        setCustomText("");
        setShowInput(false);
      }
    } else {
      setSelectedValue("");
      setCustomText("");
      setShowInput(false);
    }
  }, [value]);

  const handleDropdownChange = (newValue) => {
    if (newValue === "Other") {
      // Set ref BEFORE state updates (synchronous)
      selectingOtherRef.current = true;
      
      // Set the value first, then show input and keep dropdown open
      setSelectedValue("Other");
      setShowInput(true);
      setIsOpen(true);
      
      // Don't call onValueChange with empty string yet
      // Wait for user to type something
      if (customText) {
        onValueChange(customText);
      }
    } else {
      // Pass the dropdown value directly and close
      setSelectedValue(newValue);
      setShowInput(false);
      setCustomText("");
      onValueChange(newValue);
      setIsOpen(false);
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setCustomText(newText);
    onValueChange(newText);
  };

  const handleOpenChange = (open) => {
    // If we're trying to close but we just selected "Other", prevent closing
    if (!open && selectingOtherRef.current) {
      selectingOtherRef.current = false;
      // Force re-open the dropdown
      setIsOpen(true);
      return;
    }
    
    // Also prevent closing if "Other" is selected and no text entered yet
    if (!open && selectedValue === "Other" && showInput && !customText) {
      return;
    }
    
    setIsOpen(open);
  };

  // Display value: show custom text if "Other" is selected, otherwise show label
  const getDisplayValue = () => {
    if (selectedValue === "Other" && customText) {
      return customText;
    }
    if (selectedValue === "Other") {
      return "Other";
    }
    return selectedValue;
  };

  return (
    <div className="space-y-2">
      <Select
        value={selectedValue}
        onValueChange={handleDropdownChange}
        disabled={item?.disabled || disabled}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <SelectTrigger
          id={item?.pathToValue}
          className={cn(
            "w-full",
            error ? "border-red-500 hover:border-red-500 focus:border-red-500" : "border-gray-300",
            className
          )}
        >
          <SelectValue placeholder={placeholder}>
            {getDisplayValue() || placeholder}
          </SelectValue>
        </SelectTrigger>

        <SelectContent 
          position="popper" 
          side="bottom" 
          align="start"
          onCloseAutoFocus={(e) => {
            // Prevent auto-focus behavior that might interfere
            if (selectedValue === "Other" && showInput) {
              e.preventDefault();
            }
          }}
        >
          {options.map((opt, idx) => {
            const val =
              typeof opt === "object" ? String(opt.value ?? "") : String(opt);
            const label =
              typeof opt === "object"
                ? String(opt.label ?? opt.value ?? "")
                : String(opt);
            return (
              <SelectItem 
                key={val ?? idx} 
                value={val}
              >
                {label}
              </SelectItem>
            );
          })}
          
          {/* Show text input inside dropdown when "Other" is selected */}
          {showInput && selectedValue === "Other" && (
            <div 
              className="p-2 pt-2" 
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Input
                type="text"
                value={customText}
                onChange={handleTextChange}
                onKeyDown={(e) => {
                  // Close dropdown on Enter
                  if (e.key === "Enter" && customText) {
                    setIsOpen(false);
                  }
                  // Prevent select from handling keyboard events
                  e.stopPropagation();
                }}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Please specify..."
                disabled={item?.disabled || disabled}
                className={cn(
                  "w-full h-8",
                  error ? "border-red-500 focus:border-red-500" : "border-gray-300"
                )}
                autoFocus
              />
            </div>
          )}
        </SelectContent>
      </Select>

      {/* Show error/helper text */}
      {(error || helperText) && (
        <p className={cn("text-sm", error ? "text-red-500" : "text-gray-500")}>
          {error || helperText}
        </p>
      )}
    </div>
  );
}
